"use client"

import useEmblaCarousel from "embla-carousel-react"
import Image from "next/image"
import Link from "next/link"
import LocalizedClientLink from "@/components/molecules/LocalizedLink/LocalizedLink"
import { useCallback, useEffect, useRef, useState } from "react"
import { cn } from "@/lib/utils"
import { CompactProductCard } from "../../Product/CompactProductCard"
import { useTranslate } from "@tolgee/react"
import { SaleChannelCarouselSlide } from "@/types/sales-channel"
import Autoplay from "embla-carousel-autoplay"

export function HeroCarousel(
  {
    slides = [],
    autoPlay,
    className,
    locale
  }: {
    slides: SaleChannelCarouselSlide[]
    autoPlay?: boolean
    className?: string
    locale?: string
  }
) {
  const { t } = useTranslate()

  const plugins = !autoPlay ? [Autoplay()] : []
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "center",
    skipSnaps: false,
    inViewThreshold: 0.7,
    watchDrag: true,
    watchResize: true,
    watchSlides: false,
    watchFocus: true,
  }, plugins)

  const [selectedIndex, setSelectedIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  const scrollTo = useCallback(
    (index: number) => {
      emblaApi?.scrollTo(index)
    },
    [emblaApi]
  )

  const scrollPrev = useCallback(() => {
    emblaApi?.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext()
  }, [emblaApi])

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return

    onSelect()
    emblaApi.on("select", onSelect)
    emblaApi.on("reInit", onSelect)

    return () => {
      emblaApi.off("select", onSelect)
      emblaApi.off("reInit", onSelect)
    }
  }, [emblaApi, onSelect])

  if (!slides || slides.length < 1) {
    return null
  }

  const haloColors = [
    'bg-sky-200',
    'bg-fuchsia-200',
    'bg-emerald-200',
    'bg-amber-200',
    'bg-teal-200',
    'bg-rose-200',
    'bg-cyan-200',
    'bg-violet-200',
    'bg-silver-200',
  ]

  return (
    <div
      ref={containerRef}
      className={cn("relative bg-secondary/5", className)}
      role="region"
      aria-roledescription="carousel"
      aria-label="Featured products carousel"
    >
      <div
        ref={emblaRef}
        className="overflow-hidden md:snap-x md:snap-mandatory md:scroll-smooth md:[-webkit-overflow-scrolling:touch] md:scrollbar-none md:[scrollbar-width:none] md:[&::-webkit-scrollbar]:hidden motion-safe:scroll-smooth"
      >
        <div className="flex">
          {slides.map((slide, index) => (
            <div
              key={`h_sl_${index}`}
              className="relative min-w-full flex-[0_0_100%] @container md:snap-start md:snap-always"
              role="group"
              aria-roledescription="slide"
              aria-label={`Slide ${index + 1} of ${slides.length}`}
            >
              <div className="mx-auto">
                <div className="relative  grid min-h-0 max-w-[1600px] grid-cols-2 items-center gap-12 h-[400px] xl:h-[400px] 2xl:h-[500px]">
                  <div className="absolute inset-0 -z-10 hidden">
                    {/* <div className={`absolute inset-0 ${haloColors[index]} bg-gradient-to-b from-yellow-50/90 via-purple-400/25 to-gray-400/90`} /> */}
                    <Image
                      src={slide.imageUrl}
                      alt=""
                      width={768}
                      height={500}
                      className="size-full object-cover object-center opacity-40"
                      priority={index === 0}
                      quality={30}
                      sizes="(max-width: 768px) 50vw, 100vw"
                    />
                  </div>

                  <div className="flex flex-col justify-center space-y-8 text-left relative h-full">
                    <div className="space-y-4">
                      <h2 className="font-bakbak text-[56px] 2xl:text-[72px] leading-[1.2] tracking-[-0.2px] text-[#0b0f0e]">
                        {slide.title}
                      </h2>
                      <p className="mx-0 max-w-[585px] text-[18px] leading-[1.6] text-[#313736] font-semibold">
                        {slide.subtitle}
                      </p>
                    </div>

                    <div className="flex flex-row gap-4 justify-start">
                      <Link
                        href={slide.linkHref}
                        className="bg-[#090909] text-white font-bakbak text-[18px] leading-[1.4] tracking-[-0.2px] rounded-lg w-[158px] h-[46px] 2xl:w-[190px] 2xl:h-[54px] flex items-center justify-center hover:bg-[#1a1a1a] transition-colors"
                      >
                        {t('home_hero_carousel.shop_now')}
                      </Link>
                      <LocalizedClientLink
                        href="/categories"
                        className="border border-[#090909] text-[#090909] font-bakbak text-[18px] leading-[1.4] tracking-[-0.2px] rounded-lg w-[158px] h-[46px] 2xl:w-[190px] 2xl:h-[54px] flex items-center justify-center hover:bg-gray-50 transition-colors"
                      >
                        {t('home_hero_carousel.link_all_categories')}
                      </LocalizedClientLink>
                    </div>
                  </div>

                  <div className="relative flex items-end justify-center h-full">
                    <div className="relative h-[400px] w-[475px] xl:w-[675px] xl:h-[400px] 2xl:w-[920px] 2xl:h-[500px] overflow-hidden">
                      <Image
                        src={slide.imageUrl}
                        alt={slide.imageAlt}
                        fill
                        className="object-cover"
                        priority={index === 0}
                        quality={85}
                        sizes="(min-width: 1920px) 920px, (min-width: 1440px) 675px, 475px"
                      />

                      {slide.product && (
                        <div className="absolute bottom-8 right-8 block w-[240px]">
                          <CompactProductCard
                            product={slide.product}
                            apiProduct={slide.product}
                            {...slide.product}
                            priority={index === 0}
                            className="bg-background/95 shadow-xl backdrop-blur-sm"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 指示器 - 左下角 */}
      <div className="absolute  bottom-[24px] flex gap-2" role="tablist">
        {slides.map((_, index) => (
          <button
            key={index}
            className={cn(
              "rounded-full transition-all duration-300",
              selectedIndex === index
                ? "w-8 h-2 bg-[#090909]"
                : "w-2 h-2 bg-[rgba(28,28,26,0.1)] hover:bg-[rgba(28,28,26,0.2)]"
            )}
            onClick={() => scrollTo(index)}
            role="tab"
            aria-selected={selectedIndex === index}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* 导航按钮 - 右下角 */}
      <div className="absolute right-[0] bottom-[24px] flex gap-2">
        <button
          className={cn(
            "flex h-9 w-9 items-center justify-center rounded-full border border-[rgba(28,28,26,0.1)] bg-[rgba(255,255,255,0.8)] backdrop-blur transition-all shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_0px_rgba(0,0,0,0.1)]",
            "hover:scale-110 hover:bg-white",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          )}
          onClick={scrollPrev}
          aria-label="Previous slide"
        >
          <svg className="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button
          className={cn(
            "flex h-9 w-9 items-center justify-center rounded-full bg-[#090909] transition-all shadow-[0px_1px_1.5px_rgba(0,0,0,0.1),0px_1px_1px_rgba(0,0,0,0.1)]",
            "hover:scale-110 hover:bg-[#1a1a1a]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          )}
          onClick={scrollNext}
          aria-label="Next slide"
        >
          <svg className="size-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

    </div>
  )
}